import React, { useState } from 'react';
import moment from 'moment';
import { Box, Button, Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useSnackbar } from 'notistack';
import { GridColumns, GridSortModel } from '@mui/x-data-grid';
import { ExtendIcon } from '@/common/icons/ExtendIcon';
import { ExpireIcon } from '@/common/icons/ExpireIcon';
import AuthUser from '@/common/utils/AuthUser';
import AuthUserStore from '@/common/utils/AuthUser';
import CreditExpModel from './CreditExpModel';
import AddCreditsModal from './AddCreditsModal';
import ExtendCreditsModal from './ExtendCreditsModal';
import { callRestGetEndpoint } from '@/common/utils/api';
import { convertToStoreLocalTime } from '@/common/utils/time';
import { isUnauthorizedStudio } from '@/common/utils/creditUtils';
import { RestoreDataGrid } from '@/RestoreComponent/DataGrid';

const getQueryParams = (
  customerAllData: CustomerData,
  page: number,
  pageSize: number
) => ({
  customer_id: customerAllData?.id,
  orderBy: 'issue_date',
  pageSize: pageSize,
  page: page // API uses 0-based pagination
});

const initialSort: GridSortModel = [{ field: 'issue_date', sort: 'desc' }];

export default function RemainingCredits({
  customerAllData
}: RemainingCreditsProps) {
  const { enqueueSnackbar } = useSnackbar();
  const currentUserStoreId = AuthUserStore.getStoreId()?.toString();

  const [credits, setCredits] = useState<Credit[]>([]);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [addCreditsModalOpen, addCreditModalOpen] = useState(false);
  const [expiredCreditModalOpen, setExpiredCreditModalOpen] = useState(false);
  const [extendModalOpen, setExtendModalOpen] = useState(false);
  const [selectedData, setSelectedData] = useState<Credit | null>(null);
  const [selectedRow, setSelectedRow] = useState<Credit | null>(null);
  const [modalMode, setModalMode] = useState<'extend' | 'expire'>('extend');

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>(initialSort);

  const loggedInUser = AuthUser.getAuthUser();

  const creditExpModelOpen = () => {
    setExpiredCreditModalOpen(true);
  };

  const creditExpModelClose = () => {
    setExpiredCreditModalOpen(false);
  };

  const getCredits = async () => {
    setCreditsLoading(true);
    try {
      const res = await callRestGetEndpoint(
        getQueryParams(customerAllData, page, pageSize),
        '/pos_customer/credits/remaining'
      );
      setCredits(res?.data || []);
      setTotalCount(res?.totalCount || 0);
    } catch (e) {
      enqueueSnackbar('A problem was encountered fetching remaining credits.', {
        variant: 'error'
      });
    } finally {
      setCreditsLoading(false);
    }
  };

  React.useEffect(() => {
    getCredits();
  }, [page, pageSize]);

  const isExtendDisabled = (row: Credit) => {
    const originalExpiry = row.original_expiry || row.expires;
    const maxExtensionDate = moment(originalExpiry).add(60, 'days');
    const creditAge = moment().diff(moment(row.issue_date), 'months');
    const isOutsideExtensionWindow =
      moment().diff(moment(row.expires), 'days') > 60;
    const unauthorized = isUnauthorizedStudio(
      row.purchase_store_id,
      currentUserStoreId
    );

    if (creditAge >= 12) {
      return true;
    }

    return (
      moment(row.expires).isSameOrAfter(maxExtensionDate) ||
      row.is_refunded ||
      isOutsideExtensionWindow ||
      unauthorized
    );
  };

  const getExtendTooltip = (row: Credit) => {
    const originalExpiry = row.original_expiry || row.expires;
    const maxExtensionDate = moment(originalExpiry).add(60, 'days');
    const creditAge = moment().diff(moment(row.issue_date), 'months');
    const isOutsideExtensionWindow =
      moment().diff(moment(row.expires), 'days') > 60;
    const unauthorized = isUnauthorizedStudio(
      row.purchase_store_id,
      currentUserStoreId
    );

    if (moment(row.expires).isSameOrAfter(maxExtensionDate)) {
      return 'Maximum extension reached (60 days from original expiry)';
    }
    if (creditAge >= 12) {
      return 'Credit exceeds age limit (12 months)';
    }
    if (row.is_refunded) {
      return 'Credit was refunded';
    }
    if (isOutsideExtensionWindow) {
      return 'Outside extension window (60 days after expiry)';
    }
    if (unauthorized) {
      return 'Unauthorized studio';
    }
    return 'Extend credit expiry date';
  };

  const getExpireTooltip = (row: Credit) => {
    const unauthorized = isUnauthorizedStudio(
      row.purchase_store_id,
      currentUserStoreId
    );
    if (unauthorized) {
      return 'Unauthorized studio';
    }
    return 'Expire credit';
  };

  const handleExpire = (row: Credit) => {
    setSelectedData(row);
    setSelectedRow(row);
    setModalMode('expire');
    setExtendModalOpen(true);
  };

  const handleExtend = (row: Credit) => {
    setSelectedRow(row);
    setModalMode('extend');
    setExtendModalOpen(true);
  };

  const handleEdit = (row: Credit) => {
    setSelectedData(row);
    creditExpModelOpen();
  };

  const columnConfig: GridColumns = [
    {
      field: 'quantity',
      headerName: 'Quantity',
      flex: 0.4,
      sortable: false
    },
    {
      field: 'issue_date',
      headerName: 'Date Issued',
      flex: 1.1,
      sortable: false,
      renderCell: ({ row }) =>
        convertToStoreLocalTime(row.issue_date, 'MM/DD/YYYY')
    },
    {
      field: 'source',
      headerName: 'Source',
      flex: 1,
      sortable: false
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      sortable: false
    },
    {
      field: 'expires',
      headerName: 'Upcoming Expiry Date',
      flex: 1.1,
      sortable: false,
      renderCell: ({ row }) =>
        convertToStoreLocalTime(row.expires, 'MM/DD/YYYY')
    },
    {
      field: 'freeze_expires',
      headerName: 'Frozen Credit',
      flex: 1.1,
      sortable: false,
      renderCell: ({ row }) =>
        row.freeze_expires
          ? `Until ${convertToStoreLocalTime(row.freeze_expires, 'MM/DD/YYYY')}`
          : ''
    },
    {
      field: 'reason',
      headerName: 'Reason',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        const reasonText = row?.last_update_reason || '';
        return <div className="ellipsify" title={reasonText}>{reasonText}</div>;
      }
    },
    {
      field: '_expire_action',
      headerName: 'Expire',
      width: 60,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Tooltip title={getExpireTooltip(row)}>
            <span>
              <ExpireIcon
                style={{
                  cursor: isUnauthorizedStudio(
                    row.purchase_store_id,
                    currentUserStoreId
                  )
                    ? 'not-allowed'
                    : 'pointer',
                  opacity: isUnauthorizedStudio(
                    row.purchase_store_id,
                    currentUserStoreId
                  )
                    ? 0.5
                    : 1
                }}
                onClick={() =>
                  !isUnauthorizedStudio(
                    row.purchase_store_id,
                    currentUserStoreId
                  ) && handleExpire(row)
                }
              />
            </span>
          </Tooltip>
        </Box>
      )
    },
    {
      field: '_extend_action',
      headerName: 'Extend',
      width: 80,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Tooltip title={getExtendTooltip(row)}>
            <span>
              <ExtendIcon
                style={{
                  cursor: isExtendDisabled(row) ? 'not-allowed' : 'pointer',
                  opacity: isExtendDisabled(row) ? 0.5 : 1
                }}
                onClick={() => !isExtendDisabled(row) && handleExtend(row)}
              />
            </span>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<VisibilityIcon />}
          onClick={() => addCreditModalOpen(true)}
        >
          Add Credits
        </Button>
      </Box>

      <RestoreDataGrid
        rowCount={totalCount}
        columnConfig={columnConfig}
        rows={credits}
        loading={creditsLoading}
        page={page}
        pageSize={pageSize}
        pageSizes={[10, 50, 100]}
        sortModel={sortModel}
        onPageChange={p => setPage(p)}
        onPageChangeSize={ps => {
          setPageSize(ps);
          setPage(0);
        }}
        onSortModelChange={model => setSortModel(model)}
      />

      {addCreditsModalOpen && (
        <AddCreditsModal
          open={addCreditsModalOpen}
          customerId={customerAllData?.id}
          closeModal={refresh => {
            if (refresh) {
              getCredits();
            }
            addCreditModalOpen(false);
          }}
        />
      )}
      {expiredCreditModalOpen && (
        <CreditExpModel
          selectedData={selectedData}
          isModalOpen={expiredCreditModalOpen}
          creditExpModelClose={creditExpModelClose}
          creditUpdateSuccess={getCredits}
          customerAllData={customerAllData}
        />
      )}
      {extendModalOpen && selectedRow && (
        <ExtendCreditsModal
          open={extendModalOpen}
          onClose={() => {
            setExtendModalOpen(false);
            setSelectedRow(null);
          }}
          bundleName={selectedRow?.source || ''}
          ingredient={selectedRow?.description || ''}
          expiryDate={selectedRow?.expires || ''}
          onSave={() => {
            setExtendModalOpen(false);
            setSelectedRow(null);
            getCredits();
            enqueueSnackbar(
              modalMode === 'extend'
                ? 'Credit successfully reactivated\nReview it in Remaining Credits now.'
                : 'Credit successfully expired\nView it in Expired Credits now.',
              {
                variant: 'success',
                style: { whiteSpace: 'pre-line' }
              }
            );
          }}
          mode={modalMode}
          selectedCredit={selectedRow}
          customerId={customerAllData?.id}
        />
      )}
    </>
  );
}
